import type { ReactNode } from 'react'
import { useKitchensink } from '../context'
import { theme } from '../theme'

const codeStyle = {
  fontFamily: 'monospace',
  fontSize: '12px',
  color: theme.accent,
  backgroundColor: theme.accentSubtle,
  borderRadius: '4px',
  padding: '1px 5px',
} as const

type StepProps = { n: number; children: ReactNode }

const Step = ({ n, children }: StepProps) => (
  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
    <span
      style={{
        flexShrink: 0,
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        backgroundColor: theme.accentSubtle,
        color: theme.accent,
        fontSize: '11px',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '1px',
      }}
    >
      {n}
    </span>
    <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0, lineHeight: 1.6 }}>
      {children}
    </p>
  </div>
)

export const FileDownloadPage = () => {
  const { variant } = useKitchensink()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '36px' }}>
      <div>
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 700,
            color: theme.text,
            margin: '0 0 6px',
            letterSpacing: '-0.02em',
          }}
        >
          📥 File Download Tracking Page
        </h1>
        <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0 }}>
          Route:{' '}
          <code style={codeStyle}>{variant === 'spa' ? '/file-download' : '/file-download/'}</code>
        </p>
      </div>

      <div
        style={{
          backgroundColor: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: '10px',
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        <p
          style={{
            fontSize: '11px',
            fontWeight: 600,
            color: theme.accent,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            margin: '0 0 4px',
          }}
        >
          How to test
        </p>
        <Step n={1}>
          Fill in the tenant credentials above and click{' '}
          <strong style={{ color: theme.text }}>Initialize</strong> to start the snippet.
        </Step>
        <Step n={2}>
          Click one of the file links below. Open the browser console — you should see a{' '}
          <code style={codeStyle}>[jitsu] track FileDownload</code> log with{' '}
          <code style={codeStyle}>file_extension</code>, <code style={codeStyle}>file_name</code>,{' '}
          <code style={codeStyle}>link_text</code>, and <code style={codeStyle}>link_url</code>.
        </Step>
        <Step n={3}>
          Click a non-file link (e.g. <strong style={{ color: theme.text }}>Home</strong> in the
          nav). No <code style={codeStyle}>FileDownload</code> event should fire.
        </Step>
        <Step n={4}>
          Inspect the Jitsu network request — the event name should be{' '}
          <code style={codeStyle}>FileDownload</code> and the properties should match what was
          logged.
        </Step>
      </div>

      <div
        style={{
          backgroundColor: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: '10px',
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <p
          style={{
            fontSize: '11px',
            fontWeight: 600,
            color: theme.accent,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            margin: '0 0 4px',
          }}
        >
          Test links
        </p>
        {[
          { href: 'https://example.com/docs/whitepaper.pdf', label: 'Download Whitepaper (PDF)' },
          { href: 'https://example.com/assets/report.xlsx', label: 'Download Report (XLSX)' },
          { href: 'https://example.com/slides/overview.pptx', label: 'Download Slides (PPTX)' },
          { href: 'https://example.com/data/export.csv', label: 'Download Data (CSV)' },
          { href: 'https://example.com/archive.zip', label: 'Download Archive (ZIP)' },
        ].map(({ href, label }) => (
          <a
            key={href}
            href={href}
            onClick={e => e.preventDefault()}
            style={{
              color: theme.accent,
              fontSize: '13px',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {label}
          </a>
        ))}
      </div>

      <div
        style={{
          backgroundColor: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: '10px',
          padding: '20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <p
          style={{
            fontSize: '11px',
            fontWeight: 600,
            color: theme.accent,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            margin: '0 0 4px',
          }}
        >
          Event shape
        </p>
        <pre
          style={{
            margin: 0,
            fontFamily: 'monospace',
            fontSize: '12px',
            color: theme.text,
            backgroundColor: theme.surfaceElevated,
            borderRadius: '8px',
            padding: '14px 16px',
            overflowX: 'auto',
            lineHeight: 1.6,
          }}
        >{`jitsu.track('FileDownload', {
  file_extension: '.pdf',
  file_name: 'whitepaper.pdf',
  link_text: 'Download Whitepaper',
  link_url: 'https://example.com/docs/whitepaper.pdf',
})`}</pre>
      </div>
    </div>
  )
}
